// src/pages/QuizDetails.js
import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { getQuestionsByQuizId, updateQuestion, deleteQuestion,toggleQuestionStatus } from "../services/api"; // Import the delete API function

const QuizDetails = () => {
  const { id } = useParams(); // Get the quiz ID from the URL
  const location = useLocation(); // Get location object to access passed state
  const { quiz_name, quiz_description } = location.state || {}; // Destructure quiz name and description from state
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for question editing
  const [editQuestion, setEditQuestion] = useState(null); // Track the question being edited
  const [updatedQuestionText, setUpdatedQuestionText] = useState("");
  const [updatedOptions, setUpdatedOptions] = useState(["", "", "", ""]);
  const [updatedAnswer, setUpdatedAnswer] = useState("");

  // State for delete confirmation
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);

  // Fetch questions for the specific quiz
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await getQuestionsByQuizId(id);
        setQuestions(response.data.data);
      } catch (err) {
        setError("Failed to fetch quiz details");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [id]);

  // Handle question edit initiation
  const handleEditQuestion = (question) => {
    setEditQuestion(question);
    setUpdatedQuestionText(question.question);
    setUpdatedOptions(
      question.options.map((opt) => ({
        text: opt.text,
        is_correct: opt.is_correct,
      }))
    );
  };
  

  // Handle updating a question
  const handleUpdateQuestion = async () => {
    const updatedData = {};
    updatedData.question_id = editQuestion.question_id; 
    // Update question text if it has changed
    if (updatedQuestionText) updatedData.question = updatedQuestionText;
  
    // Update options: Convert updatedOptions to the correct format
    if (updatedOptions.length) {
      updatedData.options = updatedOptions.map((option, index) => ({
        text: option.text, // Option text
        is_correct: option.is_correct, // Whether the option is correct ("Y" or "N")
        sequence: index + 1, // Sequence number starting from 1
      }));
    }
  
    // Update the correct option
    const correctOption = updatedOptions.find((option) => option.is_correct === "Y");
    if (correctOption) {
      updatedData.correct_option = updatedOptions.indexOf(correctOption) + 1; // Correct option is 1-based
    }
  
    // Add other fields like tags, hint, language, level, and active state
    updatedData.tags = ["geography", "capital cities"]; // Example static tags
    updatedData.hint = "It is known as the City of Light."; // Example static hint
    updatedData.language = "en_US"; // Default language
    updatedData.level = { level_no: 1, level_name: "beginner" }; // Example level
    updatedData.is_active = 1; // Active state (1 for active)
  
    try {
      // Call the API to update the question
      await updateQuestion(updatedData);
  
      // Update the questions state with the updated data
      setQuestions(
        questions.map((q) =>
          q.question_id === editQuestion.question_id ? { ...q, ...updatedData } : q
        )
      );
  
      // Close the edit modal
      setEditQuestion(null);
    } catch (err) {
      setError("Failed to update question");
      console.error(err);
    }
  };
  

  // Handle delete confirmation initiation
  const handleDeleteQuestion = (question) => {
    setQuestionToDelete(question);
    setDeleteConfirmation(true);
  };

  // Confirm deletion of a question
  const confirmDeleteQuestion = async () => {
    if (!questionToDelete) return;

    try {
      await deleteQuestion(questionToDelete.question_id);
      setQuestions(questions.filter((q) => q.question_id !== questionToDelete.question_id));
      setDeleteConfirmation(false);
      setQuestionToDelete(null);
    } catch (err) {
      setError("Failed to delete question");
      console.error(err);
    }
  };

  const handleToggleStatus = async (questionId, currentStatus) => {
    try {
      const updatedStatus = currentStatus === 1 ? 0 : 1; // Toggle the status (0 for inactive, 1 for active)
      await toggleQuestionStatus(questionId, updatedStatus); // API call to toggle status
      setQuestions(questions.map((q) =>
        q.question_id === questionId ? { ...q, is_active: updatedStatus } : q
      ));
    } catch (err) {
      setError("Failed to update question status");
      console.error(err);
    }
  };

  // Cancel deletion process
  const cancelDelete = () => {
    setDeleteConfirmation(false);
    setQuestionToDelete(null);
  };

  if (loading) return <p>Loading quiz details...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>{quiz_name}</h1>
      <p style={styles.description}>{quiz_description}</p>
  
      {questions.length === 0 ? (
        <p>No questions available for this quiz.</p>
      ) : (
        questions.map((question) => (
          <div key={question.question_id} style={styles.questionContainer}>
            {/* Display tags */}
            <div style={styles.tagsContainer}>
              <strong>Tags: </strong>
              {question.tags && question.tags.length > 0
                ? question.tags.join(", ")
                : "No tags available"}
            </div>
            {/* Display level */}
            <div style={styles.metadataContainer}>
              <strong>Level: </strong>
              {question.level?.level_name || "Not specified"}
            </div>
            {/* Display hint */}
            {question.hint && (
              <div style={styles.metadataContainer}>
                <strong>Hint: </strong>
                {question.hint}
              </div>
            )}
            {/* Display language */}
            <div style={styles.metadataContainer}>
              <strong>Language: </strong>
              {question.language || "Not specified"}
            </div>
            <div style={styles.metadataContainer}>
              <strong>project: </strong>
              {question.project || "Not specified"}
            </div>



            <h3 style={styles.question}>{question.question}</h3>
            <ul style={styles.optionsList}>
              {question.options.map((option, index) => (
                <li key={index} style={{ color: option.is_correct === "Y" ? "green" : "black" }}>
                  {`${index + 1}. ${option.text}`}
                </li>
              ))}
            </ul>
            <p style={styles.correctOption}>
              Correct Answer: {question.options.find((opt) => opt.is_correct === "Y")?.text || "N/A"}
            </p>
            <div style={styles.buttonContainer}>
              <button onClick={() => handleEditQuestion(question)} style={styles.editButton}>
                Edit
              </button>
              {/* <button onClick={() => handleDeleteQuestion(question)} style={styles.deleteButton}>
                Delete
              </button> */}
               {/* Toggle switch for active status */}
               <div>
                <label htmlFor={`toggle-status-${question.question_id}`} style={styles.toggleLabel}>
                  Active
                </label>
                <input
                  type="checkbox"
                  id={`toggle-status-${question.question_id}`}
                  checked={question.is_active === 1}
                  onChange={() => handleToggleStatus(question.question_id, question.is_active)}
                  style={styles.toggleSwitch}
                />
              </div>
            </div>
          </div>
        ))
      )}
  
    {/* Edit Question Modal */}
    {editQuestion && (
    <div style={styles.modalBackdrop}>
      <div style={styles.modal}>
        <h2>Edit Question</h2>
        <label style={styles.label}>Question Text:</label>
        <input
          type="text"
          value={updatedQuestionText}
          onChange={(e) => setUpdatedQuestionText(e.target.value)}
          style={styles.input}
        />

        <label style={styles.label}>Options:</label>
        {updatedOptions.map((option, index) => (
          <div key={index} style={{ marginBottom: "10px", textAlign: "left" }}>
  <input
    type="text"
    value={option.text}
    onChange={(e) => {
      const newOptions = [...updatedOptions];
      newOptions[index] = { ...newOptions[index], text: e.target.value };
      setUpdatedOptions(newOptions);
    }}
    style={styles.input}
  />
  <label style={{ display: "flex", alignItems: "center", marginTop: "5px" }}>
    <input
      type="radio"
      name="correctOption"
      checked={option.is_correct === "Y"}
      onChange={() => {
        const newOptions = updatedOptions.map((opt, i) => ({
          ...opt,
          is_correct: i === index ? "Y" : "N",
        }));
        setUpdatedOptions(newOptions);
      }}
      style={{ marginRight: "5px" }}
    />
    Correct
  </label>
</div>

        ))}

      <div style={styles.buttonContainer}>
        <button onClick={handleUpdateQuestion} style={styles.updateButton}>
          Update Question
        </button>
        <button onClick={() => setEditQuestion(null)} style={styles.cancelButton}>
          Cancel
        </button>
      </div>
    </div>
  </div>
)}
  
      {/* Delete Confirmation Popup */}
      {deleteConfirmation && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <h2>Confirm Deletion</h2>
            <p>Are you sure you want to delete this question?</p>
            <div style={styles.buttonContainer}>
              <button onClick={confirmDeleteQuestion} style={styles.deleteButton}>
                Yes, Delete
              </button>
              <button onClick={cancelDelete} style={styles.cancelButton}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );  
};

const styles = { 
  container: { padding: "20px", fontFamily: "Arial, sans-serif" },
  tagsContainer: {
    marginBottom: "10px",
    fontStyle: "italic",
    color: "#007bff",
  },
  metadataContainer: {
    marginBottom: "10px",
    fontStyle: "italic",
    color: "#007bff",
  },
  title: { marginBottom: "10px", fontSize: "24px" },
  description: { marginBottom: "20px", fontStyle: "italic", color: "#555", fontSize: "18px" },
  questionContainer: {
    marginBottom: "15px",
    padding: "15px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
  },
  question: { margin: "0 0 10px", fontSize: "20px" },
  optionsList: { listStyleType: "none", padding: "0" },
  correctOption: {
    marginTop: "10px",
    fontWeight: "bold",
    color: "#28a745",
  },
  buttonContainer: {
    marginTop: "15px",
    display: "flex",
    justifyContent: "space-between", // Align buttons evenly
  },
  editButton: {
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    padding: "8px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  deleteButton: {
    backgroundColor: "#dc3545",
    color: "#fff",
    border: "none",
    padding: "8px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  toggleSwitch: {
    marginLeft: "10px",
    cursor: "pointer",
  },
  toggleLabel: {
    marginLeft: "10px",
    fontWeight: "bold",
  },
  modalBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#fff",
    padding: "30px",
    borderRadius: "8px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
    width: "400px",
    textAlign: "center",
  },
  label: { marginTop: "10px", display: "block", fontWeight: "bold" },
  input: {
    width: "100%",
    padding: "10px",
    marginTop: "5px",
    borderRadius: "4px",
    border: "1px solid #ccc",
  },
  updateButton: {
    marginTop: "15px",
    padding: "10px 20px",
    backgroundColor: "#28a745",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  cancelButton: {
    marginTop: "15px",
    padding: "10px 20px",
    backgroundColor: "#6c757d",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  labelRadio: {
    display: "flex",
    alignItems: "center",
    marginTop: "5px",
    textAlign: "left",
  },
  radioInput: {
    marginRight: "5px",
  },
};

export default QuizDetails;
